import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, BarChart2, CheckCircle2, Clock,
  Lock,
  Award, FileSignature, Upload, ShieldCheck, Download, AlertCircle, Loader2, X,
  Users, Plus, Trash2,  Copy, Mail, Sparkles, BookOpen,
} from 'lucide-react';
import { adminApi } from '../services/api';
import EtatVide from '../components/EtatVide';
import { ActionsAcces, EtatAcces } from '../components/AccesParticipant';
import DomainesParticipant from '../components/DomainesParticipant';
import FormationEquipe from '../components/FormationEquipe';
import FormuleDuProjet from '../components/FormuleDuProjet';
import { ChangerRole, useComptesOrganisation } from '../components/ComptesOrganisation';
import IntervenantDuProjet from '../components/IntervenantDuProjet';
import DocumentsDemandesProjet from '../components/DocumentsDemandesProjet';
import ListeDocuments from '../components/documents/ListeDocuments';
import DossierProjet from '../components/suivi/DossierProjet';
import LectureDiagnostic from '../components/suivi/LectureDiagnostic';
import SuiviReponses, { libelleBase } from '../components/suivi/SuiviReponses';
import PlanActionsCoach from '../components/suivi/PlanActionsCoach';
import SeancesProjet from '../components/suivi/SeancesProjet';
import EchangesProjet from '../components/suivi/EchangesProjet';
import RapportsProjet from '../components/suivi/RapportsProjet';
import {
  AvisDonneesPartielles, annoncerErreur, annoncerSucces, confirmer, EtatErreur, Squelette,
  SqueletteCarte, SuiviTeleversement, useCopie, useTeleversement, validerFichier,
} from '../etats';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/* Le rapport PDF d'un diagnostic clos : celui que l'organisation
   télécharge depuis son espace. */
function BoutonRapportDiag({ diagId }) {
  const [envoi, setEnvoi] = useState(false);
  const telecharger = async () => {
    setEnvoi(true);
    try {
      await adminApi.telechargerRapportDiag(diagId);
    } catch (e) {
      annoncerErreur(e.message || "Le rapport n'a pas pu être téléchargé.");
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <button type="button" onClick={telecharger} disabled={envoi} aria-busy={envoi || undefined}
      className="w-full mb-3 px-4 py-2.5 rounded-pastille border border-[var(--border)] text-[13px] font-semibold flex items-center justify-center gap-2 text-[var(--text-main)] hover:border-[var(--accent)] transition-all disabled:opacity-50">
      {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Télécharger le rapport (PDF)
    </button>
  );
}

// L'onglet « Formation » montre les modules par domaine et l'avancement de
// chaque personne de l'équipe ; les domaines s'attribuent depuis cet onglet
// ou depuis Participants (bouton « Formation »).
const ONGLETS = ['dossier', 'diagnostics', 'actions', 'seances', 'echanges', 'rapports', 'documents', 'participants', 'formation'];
const ROLES_EQUIPE = ['SUPER_ADMIN', 'ADMIN', 'ANALYSTE'];
const ROLES_ADMINISTRATION = ['SUPER_ADMIN', 'ADMIN'];

// ── Config statuts projet ──────────────────────────────────────────────────────
const STATUS_CONFIG = {
  en_attente:        { label: 'En attente',     className: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  contrat_en_attente:{ label: 'Contrat envoyé', className: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  contrat_signe:     { label: 'Contrat signé',  className: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  en_cours:          { label: 'En cours',       className: 'text-[var(--accent-texte)] bg-[var(--vert-pale)] border-[var(--border)]' },
  certification:     { label: 'En certification', className: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  termine:           { label: 'Terminé',        className: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]' },
  annule:            { label: 'Annulé',         className: 'text-[var(--alerte)] bg-[var(--alerte-fond)] border-[var(--border)]' },
};

// ── Config statuts diagnostics ─────────────────────────────────────────────────
const STATUT_DIAG = {
  planifie: { label: 'Planifié',  color: 'text-[var(--text-muted)] bg-[var(--bg-card)] border-[var(--border)]' },
  ouvert:   { label: 'Ouvert',    color: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]' },
  en_cours: { label: 'En cours',  color: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]' },
  cloture:  { label: 'Clôturé',   color: 'text-[var(--accent-texte)] bg-[var(--vert-pale)] border-[var(--border)]' },
};

// ── Modal upload contrat ───────────────────────────────────────────────────────
function ContratModal({ projetId, contratExistant, onClose, onSuccess }) {
  const [fichier, setFichier] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const envoi = useTeleversement();
  const loading = envoi.enCours;
  const fileRef = useRef(null);

  // Le format se vérifie au choix du fichier, pas après l'envoi.
  const choisir = (f) => {
    setFichier(f);
    setError(f ? validerFichier(f, { extensions: ['.pdf'] }) || '' : '');
  };

  // Fermer pendant un envoi l'annule : pas de transfert orphelin.
  const fermer = () => { if (envoi.enCours) envoi.annuler(); onClose(); };

  const submit = async () => {
    if (!fichier) { setError('Veuillez sélectionner un fichier PDF.'); return; }
    const refus = validerFichier(fichier, { extensions: ['.pdf'] });
    if (refus) { setError(refus); return; }
    setError('');
    const fd = new FormData();
    fd.append('fichier', fichier);
    if (notes) fd.append('notes', notes);
    try {
      await envoi.lancer({
        url: `${API_BASE}/accompagnement/projets/${projetId}/contrat/`,
        donnees: fd,
        methode: contratExistant ? 'PATCH' : 'POST',
      });
      annoncerSucces(contratExistant ? 'Contrat mis à jour.' : 'Contrat déposé : l’organisation peut le signer.');
      onSuccess();
      onClose();
    } catch {
      /* l'échec s'affiche sous le formulaire (SuiviTeleversement) */
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">
            {contratExistant ? 'Mettre à jour le contrat' : 'Uploader le contrat'}
          </h3>
          <button onClick={fermer} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Fichier PDF *
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-[var(--border)] rounded-pastille p-6 text-center cursor-pointer hover:border-[var(--accent)] transition-all"
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--text-muted)]" />
              {fichier
                ? <p className="text-sm font-semibold text-[var(--accent-texte)]">{fichier.name}</p>
                : <p className="text-sm text-[var(--text-muted)]">Cliquez pour sélectionner un PDF</p>
              }
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={e => choisir(e.target.files[0] || null)}
              />
            </div>
          </div>

          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Informations complémentaires sur ce contrat..."
              className="champ resize-none"
            />
          </div>

          {error && (
            <div role="alert" className="encart encart-alerte">
              {error}
            </div>
          )}
          <SuiviTeleversement televersement={envoi} nom={fichier?.name} />
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={fermer}
            className="act act-contour flex-1">
            Annuler
          </button>
          <button onClick={submit} disabled={loading || !fichier || !!error} aria-busy={loading || undefined}
            className="act flex-1">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {loading ? 'Envoi...' : 'Uploader'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Modal émission certificat ──────────────────────────────────────────────────
function CertificatModal({ projetId, onClose, onSuccess }) {
  const [fichier, setFichier] = useState(null);
  const [numeroCert, setNumeroCert] = useState('');
  const [dateEmission, setDateEmission] = useState('');
  const [dateExpiration, setDateExpiration] = useState('');
  const [error, setError] = useState('');
  const envoi = useTeleversement();
  const loading = envoi.enCours;
  const fileRef = useRef(null);

  const choisir = (f) => {
    setFichier(f);
    setError(f ? validerFichier(f, { extensions: ['.pdf'] }) || '' : '');
  };

  const fermer = () => { if (envoi.enCours) envoi.annuler(); onClose(); };

  const submit = async () => {
    if (!fichier) { setError('Veuillez sélectionner un fichier PDF.'); return; }
    const refus = validerFichier(fichier, { extensions: ['.pdf'] });
    if (refus) { setError(refus); return; }
    if (!numeroCert) { setError('Le numéro de certificat est requis.'); return; }
    if (!dateEmission) { setError("La date d'émission est requise."); return; }
    setError('');
    const fd = new FormData();
    fd.append('fichier', fichier);
    fd.append('numero_certificat', numeroCert);
    fd.append('date_emission', dateEmission);
    if (dateExpiration) fd.append('date_expiration', dateExpiration);
    try {
      await envoi.lancer({ url: `${API_BASE}/accompagnement/projets/${projetId}/certificat/`, donnees: fd });
      annoncerSucces(`Certificat ${numeroCert} émis.`);
      onSuccess();
      onClose();
    } catch {
      /* l'échec s'affiche sous le formulaire (SuiviTeleversement) */
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">Émettre le certificat ASSO-PRO</h3>
          <button onClick={fermer} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Fichier PDF du certificat *
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-[var(--border)] rounded-pastille p-5 text-center cursor-pointer hover:border-[var(--accent)] transition-all"
            >
              <Award className="w-8 h-8 mx-auto mb-2 text-[var(--text-muted)]" />
              {fichier
                ? <p className="text-sm font-semibold text-[var(--accent-texte)]">{fichier.name}</p>
                : <p className="text-sm text-[var(--text-muted)]">Cliquez pour sélectionner un PDF</p>
              }
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden"
                onChange={e => choisir(e.target.files[0] || null)} />
            </div>
          </div>

          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Numéro de certificat *
            </label>
            <input
              type="text"
              value={numeroCert}
              onChange={e => setNumeroCert(e.target.value)}
              placeholder="ex: ASSO-PRO-2025-0001"
              className="champ"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-2">
                Date d'émission *
              </label>
              <input
                type="date"
                value={dateEmission}
                onChange={e => setDateEmission(e.target.value)}
                className="champ"
              />
            </div>
            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-2">
                Date d'expiration
              </label>
              <input
                type="date"
                value={dateExpiration}
                onChange={e => setDateExpiration(e.target.value)}
                className="champ"
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="encart encart-alerte">
              {error}
            </div>
          )}
          <SuiviTeleversement televersement={envoi} nom={fichier?.name} />
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={fermer}
            className="act act-contour flex-1">
            Annuler
          </button>
          <button onClick={submit} disabled={loading || !fichier || !numeroCert || !dateEmission}
            aria-busy={loading || undefined}
            className="act flex-1">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
            {loading ? 'Émission...' : 'Émettre'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Tab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-[13px] font-semibold border-b-2 transition-all ${
        active
          ? 'border-[var(--accent)] text-[var(--accent-texte)]'
          : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)]'
      }`}
    >
      {label}
    </button>
  );
}

/* Le diagnostic initial et le final, c'est le coach qui les lance : il le
   fait quand la relation est installée, pas à la signature. Le mi-parcours
   s'ouvre seul, à la date que le parcours détermine. */
function LancementDiagnostic({ projetId, peutAgir, onLance }) {
  const [etat, setEtat] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [erreurChargement, setErreurChargement] = useState(null);

  const charger = useCallback(() => {
    adminApi.getLancementDiagnostic(projetId)
      .then((d) => { setEtat(d); setErreurChargement(null); })
      .catch((e) => setErreurChargement(e));
  }, [projetId]);
  useEffect(() => { charger(); }, [charger]);

  if (!peutAgir) return null;
  // Sans cette réponse, le bouton « Lancer » disparaissait sans un mot.
  if (!etat && erreurChargement) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={erreurChargement} onReessayer={charger} compact />
      </div>
    );
  }
  if (!etat) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 flex items-center justify-between gap-3">
        <Squelette className="h-3 w-64" /><Squelette className="h-9 w-48 !rounded-full" />
      </div>
    );
  }
  const LIBELLE = { initial: 'initial', final: 'final' };

  const lancer = async () => {
    if (!await confirmer(`Lancer le diagnostic ${LIBELLE[etat.prochain]} ? L'organisation et ses participants seront prévenus.`)) return;
    setEnCours(true);
    try {
      await adminApi.lancerDiagnostic(projetId);
      annoncerSucces(`Diagnostic ${LIBELLE[etat.prochain]} lancé : l’organisation et ses participants sont prévenus.`);
      charger();
      onLance?.();
    } catch (e) {
      annoncerErreur(e?.message || 'Le diagnostic n’a pas pu être lancé.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 flex items-center justify-between gap-3 flex-wrap">
      <p className="text-[13px] text-[var(--text-main)]">
        {etat.peut_lancer
          ? `Le diagnostic ${LIBELLE[etat.prochain]} est prêt à être lancé.`
          : etat.raison}
      </p>
      {etat.peut_lancer && (
        <button type="button" onClick={lancer} disabled={enCours} aria-busy={enCours || undefined} className="act">
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          {enCours ? 'Lancement…' : `Lancer le diagnostic ${LIBELLE[etat.prochain]}`}
        </button>
      )}
    </div>
  );
}

function DiagCard({ diag, onCloturer, onLire, lu }) {
  const cfg = STATUT_DIAG[diag.statut] || STATUT_DIAG.ouvert;
  const [loading, setLoading] = useState(false);

  const handleCloturer = async () => {
    setLoading(true);
    try { await onCloturer(diag.id); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-[var(--bg-main)] rounded-pastille p-5 border border-[var(--border)]">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="surtitre text-[var(--text-muted)] mb-1">
            Diagnostic {{ initial: 'initial', mi_parcours: 'à mi-parcours', final: 'final' }[diag.type_diagnostic] || diag.type_diagnostic}
          </p>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.color}`}>
            {cfg.label}
          </span>
          {diag.statut === 'planifie' && diag.date_prevue && (
            <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
              S'ouvrira le {new Date(diag.date_prevue).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
        {diag.score_global !== null && diag.score_global !== undefined && (
          <div className="text-right">
            <p className="text-3xl font-semibold text-[var(--text-bold)]">{diag.score_global}%</p>
            <p className="text-xs text-[var(--text-muted)]">{diag.niveau_libelle}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Dirigeants', count: diag.nb_repondants_dirigeants },
          { label: 'Membres', count: diag.nb_repondants_membres },
          { label: 'Coach', count: diag.nb_repondants_coach },
        ].map(r => (
          <div key={r.label} className="text-center bg-[var(--bg-card)] rounded-carte p-3 shadow-carte">
            <p className="text-xl font-semibold text-[var(--text-bold)]">{r.count}</p>
            <p className="sous-titre-page">{r.label}</p>
          </div>
        ))}
      </div>

      {/* Des membres seuls suffisent : ils sont un regard à part entière. */}
      {!['cloture', 'planifie'].includes(diag.statut)
        && (diag.nb_repondants_dirigeants > 0 || diag.nb_repondants_membres > 0 || diag.nb_repondants_coach > 0) && (
        <button
          onClick={handleCloturer}
          disabled={loading}
          aria-busy={loading || undefined}
          className="act w-full"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {loading ? 'Clôture...' : 'Clôturer le diagnostic'}
        </button>
      )}

      {(diag.nb_repondants_dirigeants > 0 || diag.nb_repondants_membres > 0 || diag.nb_repondants_coach > 0) && (
        <button
          type="button"
          onClick={() => onLire(diag)}
          aria-pressed={lu}
          className={`w-full mb-3 px-4 py-2.5 rounded-pastille border text-[13px] font-semibold flex items-center justify-center gap-2 transition-all ${lu
            ? 'border-[var(--accent)] text-[var(--accent-texte)] bg-[var(--accent)]/10'
            : 'border-[var(--border)] text-[var(--text-main)] hover:border-[var(--accent)]'}`}
        >
          <Sparkles className="w-4 h-4" />
          Lecture et actions correctives
        </button>
      )}

      {diag.statut === 'cloture' && <BoutonRapportDiag diagId={diag.id} />}

      {diag.statut === 'cloture' && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {diag.score_association !== null && (
            <div className="bg-[var(--bg-card)] rounded-pastille p-2 shadow-carte">
              <p className="text-[var(--text-muted)] font-semibold">Score association</p>
              <p className="font-semibold text-[var(--text-bold)]">{diag.score_association}%</p>
            </div>
          )}
          {diag.score_expert !== null && (
            <div className="bg-[var(--bg-card)] rounded-pastille p-2 shadow-carte">
              <p className="text-[var(--text-muted)] font-semibold">Score expert</p>
              <p className="font-semibold text-[var(--text-bold)]">{diag.score_expert}%</p>
            </div>
          )}
        </div>
      )}
      {/* La base du score : les regards présents à la clôture, et leur poids. */}
      {diag.statut === 'cloture' && libelleBase(diag.base_du_calcul) && (
        <p className="text-[11px] text-[var(--text-muted)] mt-2">
          <span className="font-semibold">Calculé sur :</span> {libelleBase(diag.base_du_calcul)}
        </p>
      )}
    </div>
  );
}

/* La formation de l'équipe : le coach attribue les domaines ici, le
   dirigeant depuis son espace (mêmes composants, voir FormationEquipe). */
const apiFormation = (projetId) => ({
  equipe: (pid) => adminApi.getFormationEquipe(pid),
  domaines: {
    perimetre: async () => (await adminApi.getFormationEquipe(projetId)).domaines,
    lire: (id) => adminApi.getDomainesParticipant(id),
    ecrire: (id, ids) => adminApi.setDomainesParticipant(id, ids),
  },
  attestation: (pid, domaineId, participantId) => adminApi.telechargerAttestation(pid, domaineId, participantId),
});

/* ── Inline sub-components for participants tab ─────────────────────────────

   `PwdCell` a ete retiree : elle devoilait le mot de passe d'un participant,
   que le serveur conservait en clair uniquement pour l'afficher ici. Un mot
   de passe que l'equipe peut lire n'en est plus un. L'acces se transmet
   desormais par un lien a usage unique. */

function EmailParticipantModal({ participant, onClose, onSaved }) {
  const [email, setEmail] = useState(participant.email || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const enregistrer = async (e) => {
    e.preventDefault();
    const valeur = email.trim();
    if (!valeur) { setErreur("Saisissez une adresse e-mail."); return; }
    setEnvoi(true);
    setErreur('');
    try {
      const maj = await adminApi.updateParticipant(participant.id, { email: valeur });
      annoncerSucces(`Adresse de ${participant.nom_affichage} enregistrée.`);
      onSaved(maj);
      onClose();
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-cadre w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-black text-[var(--text-bold)]">Adresse e-mail</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1 font-semibold">{participant.nom_affichage}</p>
          </div>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>
        <form onSubmit={enregistrer} className="space-y-4">
          <div>
            <label className="block text-[11px] font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">Adresse e-mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="prenom.nom@exemple.org"
              className="champ"
            />
            <p className="text-[11px] text-[var(--text-muted)] mt-2 font-semibold leading-relaxed">
              Elle sert à lui envoyer son lien de connexion et à lui ouvrir ses modules sur l'Academy.
            </p>
          </div>
          {erreur && <p className="text-[var(--alerte)] text-xs font-bold">{erreur}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="act act-contour flex-1">
              Annuler
            </button>
            <button type="submit" disabled={envoi} aria-busy={envoi || undefined}
              className="act flex-1">
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function AddParticipantModal({ projetId, onClose, onAdded }) {
  const [nom, setNom] = useState('');
  const [role, setRole] = useState('MEMBRE');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acces, setAcces] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!nom.trim()) { setError('Le nom est requis.'); return; }
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.createParticipant(projetId, { nom_affichage: nom.trim(), role, email: email.trim() });
      onAdded(result);
      // Sans adresse, des accès sont générés : on les montre une seule fois.
      // L'écran qui les affiche tient lieu de confirmation.
      if (result?.acces_a_transmettre) { setAcces(result.acces_a_transmettre); return; }
      annoncerSucces(email.trim()
        ? `${nom.trim()} est ajouté : son lien de connexion lui est envoyé.`
        : `${nom.trim()} est ajouté.`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">Ajouter un participant</h3>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>
        {acces ? (
          <div className="space-y-4">
            <p className="text-[13px] text-[var(--text-main)]">
              Accès de {nom}. Ils ne seront plus affichés : transmettez-les maintenant.
              À sa première connexion, le participant choisira son mot de passe.
            </p>
            <dl className="rounded-pastille border border-[var(--border)] p-4 space-y-2">
              <div className="flex justify-between gap-3"><dt className="text-[12px] text-[var(--text-muted)]">Identifiant</dt><dd className="font-mono text-[13px]">{acces.identifiant}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-[12px] text-[var(--text-muted)]">Mot de passe</dt><dd className="font-mono text-[13px]">{acces.mot_de_passe}</dd></div>
            </dl>
            <button type="button" className="act w-full" onClick={onClose}>J'ai noté</button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">Nom complet *</label>
            <input
              value={nom}
              onChange={e => setNom(e.target.value)}
              placeholder="Prénom Nom"
              className="champ"
            />
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">Adresse e-mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="prenom.nom@exemple.org"
              className="champ"
            />
            <p className="text-[11px] text-[var(--text-muted)] mt-2 font-semibold">
              Avec une adresse, il reçoit son lien de connexion. Sans adresse, des accès sont générés et affichés une seule fois.
            </p>
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">Rôle</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="champ"
            >
              <option value="MEMBRE">Membre</option>
              <option value="DIRIGEANT">Dirigeant</option>
            </select>
          </div>
          {error && <p className="text-[var(--alerte)] text-xs font-bold">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="act act-contour flex-1">
              Annuler
            </button>
            <button type="submit" disabled={loading} aria-busy={loading || undefined}
              className="act flex-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Créer
            </button>
          </div>
        </form>
        )}
      </motion.div>
    </div>
  );
}

export default function ProjetDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parametres, setParametres] = useSearchParams();
  const [lectureDiag, setLectureDiag] = useState(null);
  const [projet, setProjet] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [actions, setActions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [contrat, setContrat] = useState(null);
  const [certificat, setCertificat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const dejaCharge = useRef(false);
  const [error, setError] = useState(null);
  // Les participants peuvent manquer sans que le reste du projet soit perdu.
  const [participantsManquants, setParticipantsManquants] = useState(false);
  const { copier } = useCopie();
  // L'onglet vit dans l'adresse : les notifications (seance, message, rapport)
  // ouvrent directement le bon onglet.
  const ongletDemande = parametres.get('onglet');
  const activeTab = ONGLETS.includes(ongletDemande) ? ongletDemande : 'diagnostics';
  const setActiveTab = (onglet) => setParametres({ onglet }, { replace: true });
  // Arriver avec `?onglet=` (bouton « Documents » de la liste, fiche d'une
  // organisation, notification), c'est vouloir CET onglet : il est sous le
  // contrat et le certificat, hors de l'écran. On y descend une fois, au
  // premier affichage — pas à chaque changement d'onglet fait sur place.
  const ongletsRef = useRef(null);
  const dejaDescendu = useRef(false);
  useEffect(() => {
    if (!charge || dejaDescendu.current || !ONGLETS.includes(ongletDemande)) return;
    dejaDescendu.current = true;
    const sobre = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => ongletsRef.current?.scrollIntoView({
      behavior: sobre ? 'auto' : 'smooth', block: 'start',
    }));
  }, [charge, ongletDemande]);
  const [showContratModal, setShowContratModal] = useState(false);
  const [showCertificatModal, setShowCertificatModal] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [validatingContrat, setValidatingContrat] = useState(false);
  const [deletingParticipant, setDeletingParticipant] = useState(null);
  const [modulesCible, setModulesCible] = useState(null);
  const [emailCible, setEmailCible] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let partsEchec = false;
      const [p, d, docs, acts, parts] = await Promise.all([
        adminApi.getProjet(id),
        adminApi.getDiagnosticsProjet(id),
        adminApi.getDocuments(id),
        adminApi.getActions(id),
        adminApi.getParticipants(id).catch(() => { partsEchec = true; return null; }),
      ]);
      setProjet(p);
      setDiagnostics(Array.isArray(d) ? d : []);
      setDocuments(Array.isArray(docs) ? docs : []);
      setActions(Array.isArray(acts) ? acts : []);
      // Un échec ne remplace pas la liste connue par une liste vide.
      if (!partsEchec) setParticipants(Array.isArray(parts) ? parts : []);
      setParticipantsManquants(partsEchec);

      // Fetch contrat et certificat (silencieux si 404)
      try {
        const c = await adminApi.getContrat(id);
        setContrat(c);
      } catch { setContrat(null); }

      try {
        const cert = await adminApi.getCertificat(id);
        setCertificat(cert);
      } catch { setCertificat(null); }
      setError(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue (après une action) garde la page affichée.
      if (dejaCharge.current) annoncerErreur(e.message || 'Le projet n’a pas pu être actualisé.');
      else setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Comptes de l'organisation (administration) : fusionnés au tableau des participants.
  const comptesOrganisation = useComptesOrganisation(
    id, ROLES_ADMINISTRATION.includes(user?.role), load);

  const handleCloturerDiag = async (diagId) => {
    // Avant de clore : qui manque, et le coach a-t-il donné son regard ?
    // Clôturer fige le score ; ce qui n'est pas répondu n'y entrera plus.
    let avertissement = '';
    try {
      const etat = await adminApi.getEtatReponses(diagId);
      const manques = [];
      if (etat.en_attente?.length) manques.push(`${etat.en_attente.join(', ')} n’${etat.en_attente.length > 1 ? 'ont' : 'a'} pas terminé`);
      if (!etat.reponses_coach) manques.push('le coach n’a pas répondu');
      if (manques.length) avertissement = ` Attention : ${manques.join(' et ')}.`;
    } catch { /* l'état manque : on demande quand même confirmation */ }
    if (!await confirmer(`Clôturer ce diagnostic et calculer les scores ?${avertissement}`)) return;
    try {
      await adminApi.cloturerDiagnostic(diagId);
      annoncerSucces('Diagnostic clôturé : les scores sont calculés.');
      load();
    } catch (e) {
      annoncerErreur(e.message || 'Le diagnostic n’a pas pu être clôturé.');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!await confirmer('Supprimer ce participant ? Son compte sera désactivé.')) return;
    setDeletingParticipant(participantId);
    try {
      await adminApi.deleteParticipant(participantId);
      setParticipants(prev => prev.filter(p => p.id !== participantId));
      annoncerSucces('Participant supprimé ; son compte est désactivé.');
    } catch (e) {
      annoncerErreur(e.message || 'Le participant n’a pas pu être supprimé.');
    } finally {
      setDeletingParticipant(null);
    }
  };

  const handleValiderContrat = async () => {
    if (!await confirmer('Valider le contrat signé par l\'organisation ?')) return;
    setValidatingContrat(true);
    try {
      await adminApi.validerContrat(id);
      annoncerSucces('Contrat validé.');
      load();
    } catch (e) {
      annoncerErreur(e.message || 'Le contrat n’a pas pu être validé.');
    } finally {
      setValidatingContrat(false);
    }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  // Premier chargement : la forme de la page (en-tête, formule, contrat,
  // onglets, cartes). Les rechargements après une action, eux, gardent la
  // page affichée — et les modales ouvertes.
  if (loading && !charge) {
    return (
      <div className="space-y-6" role="status" aria-live="polite" aria-label="Chargement du projet">
        <Squelette className="h-3 w-36" />
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
          <Squelette className="h-3 w-24 mb-2" />
          <Squelette className="h-8 w-72 mb-4" />
          <div className="flex gap-2">
            {[110, 130, 180].map(l => <Squelette key={l} className="h-7 !rounded-full" style={{ width: l }} />)}
          </div>
        </div>
        <SqueletteCarte />
        <SqueletteCarte />
        <div className="flex gap-4 border-b border-[var(--border)] pb-3">
          {[80, 110, 120, 80, 90].map(l => <Squelette key={l} className="h-4" style={{ width: l }} />)}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"><SqueletteCarte /><SqueletteCarte /></div>
      </div>
    );
  }

  if (error && !charge) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={error} onReessayer={load} />
      </div>
    );
  }

  if (!projet) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={{ statut: 404 }} />
      </div>
    );
  }

  // Memes regles que le serveur : l'equipe agit sur tout projet, le coach sur
  // ceux qui lui sont confies ; contrat et certificat restent a l'administration.
  const role = user?.role;
  const coachDuProjet = role === 'COACH' && String(projet.coach) === String(user?.id);
  // L'analyste consulte le suivi mais n'agit pas (table des zones du serveur).
  const estEquipe = ROLES_EQUIPE.includes(role) || coachDuProjet;
  const peutAgir = ROLES_ADMINISTRATION.includes(role) || coachDuProjet;
  const estAdministration = ROLES_ADMINISTRATION.includes(role);
  const comptesOrg = comptesOrganisation;
  const compteDe = (userId) => comptesOrg.comptes.find(c => c.id === userId);
  const horsEquipe = comptesOrg.comptes.filter(c => !participants.some(p => p.user === c.id));
  const dirigeantsHors = horsEquipe.filter(c => c.role === 'DIRIGEANT')
    .sort((a, b) => Number(b.principal) - Number(a.principal));
  const membresHors = horsEquipe.filter(c => c.role !== 'DIRIGEANT');
  const ligneCompte = (c) => (
                    <tr key={`compte-${c.id}`} className="hover:bg-[var(--bg-main)] transition-colors">
                      <td data-libelle="Nom" className="px-5 py-4">
                        <div className="text-sm font-bold text-[var(--text-bold)]">{c.nom}</div>
                        <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--text-muted)]">
                          <Mail className="w-3 h-3" />
                          {c.email || '—'}
                        </div>
                      </td>
                      <td data-libelle="Rôle" className="px-5 py-4">
                        <span className={`inline-flex px-2 py-1 rounded-controle text-[12px] font-semibold ${
                          c.role === 'DIRIGEANT'
                            ? 'text-[var(--text-bold)] bg-[var(--info-fond)]'
                            : 'text-[var(--accent-texte)] bg-[var(--accent)]/10 border-[var(--accent)]/30'
                        }`}>{c.role === 'DIRIGEANT' ? (c.principal ? 'DIRIGEANT PRINCIPAL' : 'DIRIGEANT') : 'MEMBRE'}</span>
                        <ChangerRole compte={c} gestion={comptesOrg} />
                      </td>
                      <td data-libelle="Adresse de connexion" className="px-5 py-4">
                        <span className="font-mono text-xs text-[var(--text-main)]">{c.username || '—'}</span>
                      </td>
                      <td data-libelle="Accès" className="px-5 py-4">
                        <span className="text-xs font-semibold text-[var(--text-muted)]">
                          {!c.actif ? 'Compte fermé : ne peut plus se connecter'
                            : c.role === 'DIRIGEANT' ? 'Accès à tout l’espace' : 'Pas encore dans l’équipe'}
                        </span>
                      </td>
                      <td data-libelle="Formation" className="px-5 py-4 text-xs font-semibold text-[var(--text-muted)]">
                        {c.role === 'DIRIGEANT' ? 'Tous les modules' : '—'}
                      </td>
                      <td data-actions="" className="px-5 py-4 text-right">
                        {!c.principal && (
                          <button type="button" onClick={() => comptesOrg.retirer(c)}
                                  disabled={comptesOrg.enCours === c.id}
                                  aria-label={`Supprimer ${c.nom}`} title="Supprimer de l’organisation"
                                  className="act act-icone act-tertiaire">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Back */}
      <button
        onClick={() => navigate('/projets')}
        className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-bold)] mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux projets
      </button>

      {/* Header projet */}
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <p className="surtitre text-[var(--text-muted)] mb-1">Projet #{projet.id}</p>
            <h1 className="titre-page mb-3">{projet.organisation_nom}</h1>
            <div className="flex flex-wrap gap-2">
              {/* Badge statut projet */}
              {(() => {
                const cfg = STATUS_CONFIG[projet.statut] || { label: projet.statut, className: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]' };
                return (
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-controle text-[12px] font-semibold border ${cfg.className}`}>
                    {cfg.label}
                  </span>
                );
              })()}
              {/* Badge type projet */}
              {projet.type_projet === 'certification' && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-controle text-[12px] font-semibold bg-[var(--info-fond)] text-[var(--text-bold)]">
                  <Award className="w-3 h-3" />
                  Certification
                </span>
              )}
              {(!projet.type_projet || projet.type_projet === 'accompagnement') && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-controle text-[12px] font-semibold bg-[var(--vert-pale)] text-[var(--vert-fonce)]">
                  <FileSignature className="w-3 h-3" />
                  Accompagnement
                </span>
              )}
              {/* Le coach se change ici : il ne se choisissait qu'à la
                  création du projet ou à l'acceptation de sa demande. */}
              <IntervenantDuProjet projet={projet} peutModifier={estAdministration}
                onChange={(maj) => setProjet((p) => ({ ...p, ...maj }))} />
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[var(--text-muted)] font-semibold">Demande reçue le</p>
            <p className="text-sm font-semibold text-[var(--text-bold)]">
              {new Date(projet.date_demande).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
        {projet.message_demande && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <p className="text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-2">Message de l'association</p>
            <p className="text-sm text-[var(--text-main)]">"{projet.message_demande}"</p>
          </div>
        )}
      </div>


      {/* La formule : base du contrat, et ce que la plateforme fait respecter. */}
      <div className="mb-6">
        <FormuleDuProjet projetId={id} peutModifier={estAdministration} />
      </div>

      {/* Section Contrat d'accompagnement */}
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-pastille bg-[var(--info-fond)] flex items-center justify-center">
              <FileSignature className="w-4 h-4 text-[var(--text-bold)]" />
            </div>
            <h2 className="titre-carte text-[var(--text-bold)]">Contrat d'accompagnement</h2>
          </div>
          {estAdministration && (
            <button
              onClick={() => setShowContratModal(true)}
              className="act act-contour"
            >
              <Upload className="w-3.5 h-3.5" />
              {contrat ? 'Mettre à jour' : 'Uploader'}
            </button>
          )}
        </div>

        {!contrat ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            <FileSignature className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm font-semibold">Aucun contrat uploadé</p>
            <p className="text-xs mt-1">Uploadez le contrat d'accompagnement pour l'envoyer à l'organisation.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Statut contrat */}
            <div className="flex flex-wrap gap-2 items-center">
              {contrat.valide_par_admin ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-controle text-[12px] font-semibold bg-[var(--vert-pale)] text-[var(--vert-fonce)]">
                  <ShieldCheck className="w-3 h-3" />
                  Validé par l'admin
                </span>
              ) : contrat.signe_par_org ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-controle text-[12px] font-semibold bg-[var(--info-fond)] text-[var(--text-bold)]">
                  <CheckCircle2 className="w-3 h-3" />
                  Signé par l'organisation
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-controle text-[12px] font-semibold bg-[var(--attention-fond)] text-[var(--attention)]">
                  <Clock className="w-3 h-3" />
                  En attente de signature
                </span>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <p className="surtitre text-[var(--text-muted)] mb-1">Créé le</p>
                <p className="font-semibold text-[var(--text-bold)]">{fmtDate(contrat.date_creation)}</p>
              </div>
              {contrat.signe_par_org && contrat.date_signature && (
                <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                  <p className="surtitre text-[var(--text-muted)] mb-1">Signé le</p>
                  <p className="font-semibold text-[var(--text-bold)]">{fmtDate(contrat.date_signature)}</p>
                </div>
              )}
            </div>

            {contrat.fichier_url && (
              <a
                href={contrat.fichier_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-pastille bg-[var(--bg-main)] border border-[var(--border)] text-[var(--accent-texte)] text-xs font-bold hover:opacity-80 transition-all"
              >
                <Download className="w-3.5 h-3.5" />
                Télécharger le contrat
              </a>
            )}

            {estAdministration && contrat.signe_par_org && !contrat.valide_par_admin && (
              <div className="encart encart-attention">
                <AlertCircle className="w-4 h-4 text-[var(--attention)] shrink-0" />
                <p className="text-xs text-[var(--attention)] font-semibold flex-1">Le contrat a été signé par l'organisation et attend votre validation.</p>
                <button
                  onClick={handleValiderContrat}
                  disabled={validatingContrat}
                  aria-busy={validatingContrat || undefined}
                  className="act shrink-0"
                >
                  {validatingContrat ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  Valider
                </button>
              </div>
            )}

            {contrat.notes && (
              <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <p className="surtitre text-[var(--text-muted)] mb-1">Notes</p>
                <p className="text-sm text-[var(--text-main)]">{contrat.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section Certificat ASSO-PRO */}
      {(['en_cours', 'certification', 'termine'].includes(projet.statut)) && (
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-pastille bg-[var(--info-fond)] flex items-center justify-center">
                <Award className="w-4 h-4 text-[var(--text-bold)]" />
              </div>
              <h2 className="titre-carte text-[var(--text-bold)]">Certificat ASSO-PRO</h2>
            </div>
            {certificat && estAdministration && (
              <button
                onClick={() => setShowCertificatModal(true)}
                className="act act-contour"
              >
                <Upload className="w-3.5 h-3.5" />
                Mettre à jour
              </button>
            )}
          </div>

          {!certificat ? (
            <div className="text-center py-8 text-[var(--text-muted)]">
              <Award className="w-10 h-10 mx-auto mb-2 opacity-30" />
              {projet.statut === 'termine' && estAdministration ? (
                <div>
                  <p className="text-sm font-semibold mb-3">Le projet est terminé — émettez le certificat.</p>
                  <button
                    onClick={() => setShowCertificatModal(true)}
                    className="act"
                  >
                    <Award className="w-4 h-4" />
                    Émettre le certificat
                  </button>
                </div>
              ) : (
                <p className="text-sm font-semibold">Le certificat sera disponible à la fin de l'accompagnement.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                  <p className="surtitre text-[var(--text-muted)] mb-1">N° Certificat</p>
                  <p className="font-semibold text-[var(--text-bold)]">{certificat.numero_certificat || '—'}</p>
                </div>
                <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                  <p className="surtitre text-[var(--text-muted)] mb-1">Date d'émission</p>
                  <p className="font-semibold text-[var(--text-bold)]">{fmtDate(certificat.date_emission)}</p>
                </div>
                {certificat.date_expiration && (
                  <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                    <p className="surtitre text-[var(--text-muted)] mb-1">Expiration</p>
                    <p className="font-semibold text-[var(--text-bold)]">{fmtDate(certificat.date_expiration)}</p>
                  </div>
                )}
              </div>

              {certificat.emis_par_nom && (
                <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                  <p className="surtitre text-[var(--text-muted)] mb-1">Émis par</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{certificat.emis_par_nom} — le {fmtDate(certificat.date_emission)}</p>
                </div>
              )}

              {certificat.fichier_url && (
                <a
                  href={certificat.fichier_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-pastille bg-[var(--bg-main)] border border-[var(--border)] text-[var(--accent-texte)] text-xs font-bold hover:opacity-80 transition-all"
                >
                  <Download className="w-3.5 h-3.5" />
                  Télécharger le certificat
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div ref={ongletsRef} className="tableau-hote flex border-b border-[var(--border)] mb-6 scroll-mt-4">
        {[
          { key: 'dossier', label: 'Dossier', visible: estEquipe },
          { key: 'diagnostics', label: `Diagnostics (${diagnostics.filter(d => d.statut === 'cloture').length})` },
          { key: 'actions', label: `Plan d'actions (${actions.length})` },
          { key: 'seances', label: 'Séances' },
          { key: 'echanges', label: 'Échanges' },
          { key: 'rapports', label: 'Rapports' },
          { key: 'documents', label: `Documents (${documents.length})` },
          { key: 'participants', label: `Participants (${participants.length})` },
          { key: 'formation', label: 'Formation' },
        ].filter(t => t.visible !== false).map(t => (
          <Tab key={t.key} label={t.label} active={activeTab === t.key} onClick={() => setActiveTab(t.key)} />
        ))}
      </div>

      {activeTab === 'dossier' && estEquipe && <DossierProjet projetId={id} />}

      {/* Tab : Diagnostics */}
      {activeTab === 'diagnostics' && (
        <div className="space-y-6">
          {/* Remonté quand un diagnostic change d'état (clôture) : sinon il
              annonçait encore « en cours » un diagnostic clos. */}
          <LancementDiagnostic key={diagnostics.map((d) => `${d.id}:${d.statut}`).join('|')}
                               projetId={id} peutAgir={peutAgir} onLance={load} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {diagnostics.length === 0 ? (
              <div className="sm:col-span-2 lg:col-span-3 rounded-carte bg-[var(--bg-card)] shadow-carte">
                <EtatVide
                  icone={BarChart2}
                  titre="Aucun diagnostic encore"
                  aide="Le diagnostic initial apparaîtra ici dès qu’il sera lancé."
                />
              </div>
            ) : (
              diagnostics.map(d => (
                <DiagCard key={d.id} diag={d} onCloturer={handleCloturerDiag}
                  lu={lectureDiag?.id === d.id}
                  onLire={(diag) => setLectureDiag(lectureDiag?.id === diag.id ? null : diag)} />
              ))
            )}
          </div>
          {/* Le diagnostic en cours, personne par personne. */}
          {diagnostics.filter((d) => ['ouvert', 'en_cours'].includes(d.statut)).map((d) => (
            <SuiviReponses key={`suivi-${d.id}`} diagnostic={d} peutAgir={peutAgir} onChange={load} />
          ))}
          {lectureDiag && (
            <LectureDiagnostic key={lectureDiag.id} diagnostic={lectureDiag} />
          )}
        </div>
      )}

      {activeTab === 'actions' && (
        <PlanActionsCoach projetId={id} diagnostics={diagnostics} actions={actions} peutAgir={peutAgir} onChange={load} />
      )}
      {activeTab === 'seances' && <SeancesProjet projetId={id} actions={actions} peutAgir={peutAgir} />}
      {activeTab === 'echanges' && <EchangesProjet projetId={id} nomAssociation={projet.organisation_nom} peutEcrire={peutAgir} />}
      {activeTab === 'rapports' && <RapportsProjet projetId={id} peutAgir={peutAgir} utilisateur={user} />}

      {/* Tab : Documents */}
      {activeTab === 'documents' && (
        <div className="space-y-3">
          <DocumentsDemandesProjet projetId={id} peutAgir={peutAgir} onChange={load} />
          <div>
            <h3 className="titre-carte mb-3">Documents déposés</h3>
            <ListeDocuments documents={documents} peutExaminer={peutAgir} onChange={load} />
          </div>
        </div>
      )}


      {/* Tab : Participants */}
      {activeTab === 'formation' && <FormationEquipe projetId={id} api={apiFormation(id)} />}

      {activeTab === 'participants' && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-xs text-[var(--text-muted)] font-semibold">
              Gérez les dirigeants et membres participant au diagnostic multi-acteurs.
            </p>
            <button
              onClick={() => setShowAddParticipant(true)}
              className="act"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter
            </button>
          </div>

          <div className="encart encart-attention mb-4">
            <AlertCircle className="w-4 h-4 text-[var(--attention)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--attention)] font-semibold">
              Chaque participant (six au plus : deux dirigeants et quatre membres) répond au diagnostic de son côté et suit les domaines de formation que vous lui ouvrez (bouton « Formation » ; avancement dans l'onglet Formation). Son accès se ferme quand il a terminé le diagnostic, sauf s'il a des modules à suivre. Ses réponses restent consultables ici.
            </p>
          </div>

          {participantsManquants && (
            <div className="mb-4">
              <AvisDonneesPartielles manquantes={['la liste des participants']} onReessayer={load} />
            </div>
          )}

          {participants.length === 0 && horsEquipe.length === 0 && !participantsManquants ? (
            <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
              <EtatVide
                icone={Users}
                titre="Aucun participant enregistré"
                aide="Ajoutez des dirigeants et membres pour le diagnostic multi-acteurs."
                action={(
                  <button type="button" className="act act-sm" onClick={() => setShowAddParticipant(true)}>
                    <Plus className="w-3.5 h-3.5" />
                    Ajouter un participant
                  </button>
                )}
              />
            </div>
          ) : participants.length === 0 && horsEquipe.length === 0 ? null : (
            <div className="bg-[var(--bg-card)] rounded-pastille shadow-carte overflow-hidden">
              <table className="tableau-cartes w-full text-left">
                <thead className="">
                  <tr>
                    {['Nom', 'Rôle', 'Adresse de connexion', 'Accès', 'Formation', ''].map(h => (
                      <th key={h} className="">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {dirigeantsHors.map(ligneCompte)}
                  {participants.map(p => (
                    <tr key={p.id} className="hover:bg-[var(--bg-main)] transition-colors">
                      <td data-libelle="Nom" className="px-5 py-4">
                        <div className="text-sm font-bold text-[var(--text-bold)]">{p.nom_affichage}</div>
                        <button
                          onClick={() => setEmailCible(p)}
                          disabled={!p.user}
                          className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--text-muted)] hover:text-[var(--accent-texte)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Mail className="w-3 h-3" />
                          {p.email || 'Renseigner une adresse e-mail'}
                        </button>
                      </td>
                      <td data-libelle="Rôle" className="px-5 py-4">
                        <span className={`inline-flex px-2 py-1 rounded-controle text-[12px] font-semibold ${
                          p.role === 'DIRIGEANT'
                            ? 'text-[var(--text-bold)] bg-[var(--info-fond)]'
                            : 'text-[var(--accent-texte)] bg-[var(--accent)]/10 border-[var(--accent)]/30'
                        }`}>{p.role}</span>
                        {estAdministration && (
                          <ChangerRole compte={compteDe(p.user)} gestion={comptesOrg} />
                        )}
                      </td>
                      <td data-libelle="Adresse de connexion" className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-[var(--text-main)]">{p.username || '—'}</span>
                          {p.username && (
                            <button onClick={() => copier(p.username, 'Identifiant copié.')} aria-label="Copier l’identifiant"
                                    className="text-[var(--text-muted)] hover:text-[var(--accent-texte)] transition-colors">
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td data-libelle="Accès" className="px-5 py-4">
                        <EtatAcces acces={p.acces} />
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <ActionsAcces participant={p} agir={adminApi.updateParticipant}
                                        onMaj={(maj) => setParticipants(prev => prev.map(x => (x.id === maj.id ? { ...x, ...maj } : x)))} />
                        </div>
                      </td>
                      <td data-libelle="Formation" className="px-5 py-4">
                        {p.role === 'DIRIGEANT' ? (
                          <span className="text-xs font-semibold text-[var(--text-muted)]">Tous les modules</span>
                        ) : (
                        <button
                          type="button"
                          onClick={() => setModulesCible(p)}
                          disabled={!p.user}
                          title={p.user ? 'Choisir les domaines de formation que ce membre suivra' : "Ce participant n'a pas de compte."}
                          className="act act-contour"
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                          Formation{p.modules?.attribues ? ` · ${p.modules.termines}/${p.modules.attribues}` : ''}
                        </button>
                        )}
                      </td>
                      <td data-actions="" className="px-5 py-4 text-right">
                        <button
                          onClick={() => handleDeleteParticipant(p.id)}
                          disabled={deletingParticipant === p.id}
                          aria-busy={deletingParticipant === p.id || undefined}
                          aria-label={`Supprimer ${p.nom_affichage}`}
                          className="act act-icone act-tertiaire"
                        >
                          {deletingParticipant === p.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {membresHors.map(ligneCompte)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {emailCible && (
        <EmailParticipantModal
          participant={emailCible}
          onClose={() => setEmailCible(null)}
          onSaved={(maj) => setParticipants(prev => prev.map(x => x.id === maj.id ? { ...x, ...maj } : x))}
        />
      )}

      {modulesCible && (
        <DomainesParticipant
          participant={modulesCible}
          api={apiFormation(id).domaines}
          onClose={() => setModulesCible(null)}
          onSaved={(resume) => setParticipants(prev => prev.map(x => (x.id === modulesCible.id ? { ...x, modules: resume } : x)))}
        />
      )}

      {showAddParticipant && (
        <AddParticipantModal
          projetId={id}
          onClose={() => setShowAddParticipant(false)}
          onAdded={(p) => setParticipants(prev => [...prev, p])}
        />
      )}
      {showContratModal && (
        <ContratModal
          projetId={id}
          contratExistant={contrat}
          onClose={() => setShowContratModal(false)}
          onSuccess={load}
        />
      )}
      {showCertificatModal && (
        <CertificatModal
          projetId={id}
          onClose={() => setShowCertificatModal(false)}
          onSuccess={load}
        />
      )}
    </motion.div>
  );
}
